import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';

import { ToastService } from 'src/app/services/toast.service';
import { AccountAuthService, PasswordChangeRequired } from '../account-auth.service';
import { newPasswordForm } from '../new-password-form';

/**
 * First sign-in after the move from Auth0: the account signed in with its
 * old password and must choose a new one before entering the app (the
 * backend gives no session until it does).
 */
@Component({
  selector: 'app-set-new-password',
  templateUrl: './set-new-password.component.html',
  styleUrls: ['../auth-form.scss'],
  standalone: false,
})
export class SetNewPasswordComponent implements OnInit {
  readonly form = newPasswordForm(this.fb);
  pending: (PasswordChangeRequired & { currentPassword: string }) | null = null;
  busy = false;
  error: string | null = null;
  passwordError: string | null = null;

  constructor(
    private fb: FormBuilder,
    private auth: AccountAuthService,
    private router: Router,
    private toasts: ToastService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.pending = this.auth.pendingChange;
    if (!this.pending) {
      // Opened directly or after a reload: the ticket lives in memory only.
      this.router.navigate(['/login'], { replaceUrl: true });
      return;
    }
    this.form.controls.password.valueChanges.subscribe(() => (this.passwordError = null));
  }

  submit(): void {
    if (this.busy || !this.pending) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const password = this.form.controls.password.value!;
    if (password === this.pending.currentPassword) {
      this.passwordError = 'Choose a different password from your current one.';
      return;
    }
    this.busy = true;
    this.error = null;
    this.auth.firstPasswordChange(this.pending.ticket, password).subscribe({
      next: (session) => {
        this.toasts.showSuccess('Your new password is set. Use it from now on.', 'success');
        this.auth.startSession(session).catch(() => this.router.navigate(['/login']));
      },
      error: (err) => {
        this.busy = false;
        const error = this.auth.toError(err);
        if (error.code === 'weak_password') {
          this.passwordError = error.message;
        } else if (error.code === 'ticket_expired') {
          this.auth.pendingChange = null;
          this.router.navigate(['/login'], { queryParams: { email: this.pending?.email, reason: 'expired' } });
          return;
        } else {
          this.error = error.message;
        }
        this.cdr.detectChanges();
      },
    });
  }

  cancel(): void {
    const email = this.pending?.email;
    this.auth.pendingChange = null;
    this.router.navigate(['/login'], { queryParams: { email } });
  }
}
